import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { SEO } from "../components/SEO";
import { TrackCard } from "../components/TrackCard";
import { Reveal } from "../components/Reveal";

const USAGE_FILTERS = ["Tous", "Pub", "Film", "Artiste"];

const Catalogue = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState("Tous");
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (usage !== "Tous") params.usage = usage;
    if (q.trim()) params.q = q.trim();
    api
      .get("/tracks", { params })
      .then((r) => setTracks(r.data))
      .catch(() => setTracks([]))
      .finally(() => setLoading(false));
  }, [usage, q]);

  return (
    <main data-testid="catalogue-page" className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8">
      <SEO title="Catalogue" path="/catalogue"
           description="Écoutez et licenciez des musiques originales pour vos films, publicités et contenus — licences claires, prix affichés, téléchargement immédiat après paiement." />
      <Reveal>
        <p className="text-xs uppercase tracking-[0.35em] text-amber-500">Catalogue</p>
        <h1 className="mt-3 font-display text-4xl font-black tracking-tighter sm:text-6xl">
          Trouvez votre <span className="italic text-amber-500">son</span>
        </h1>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-zinc-400">
          Musiques libres de droit pour vos publicités, films et productions : écoutez,
          licencez en quelques clics, téléchargez le master aussitôt.
        </p>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-10 flex flex-col gap-4 border-y border-white/[0.08] py-6 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="usage-filters">
            {USAGE_FILTERS.map((u) => (
              <button
                key={u}
                onClick={() => setUsage(u)}
                data-testid={`filter-${u.toLowerCase()}`}
                className={`border px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors ${
                  usage === u
                    ? "border-amber-500 bg-amber-500 text-black"
                    : "border-white/15 text-zinc-400 hover:border-amber-500/60 hover:text-amber-400"
                }`}
              >
                {u}
              </button>
            ))}
          </div>
          <div className="relative md:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="catalogue-search"
              placeholder="Titre, genre, humeur…"
              className="w-full border border-white/15 bg-transparent py-2.5 pl-10 pr-4 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-amber-500"
            />
          </div>
        </div>
      </Reveal>

      {loading ? (
        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalogue-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse border border-white/[0.08] bg-surface">
              <div className="aspect-square bg-white/[0.04]" />
              <div className="space-y-3 p-4">
                <div className="h-4 w-2/3 bg-white/[0.06]" />
                <div className="h-3 w-1/3 bg-white/[0.04]" />
              </div>
            </div>
          ))}
        </div>
      ) : tracks.length === 0 ? (
        <p data-testid="catalogue-empty" className="mt-20 text-center text-sm uppercase tracking-[0.25em] text-zinc-500">
          Aucun morceau ne correspond à votre recherche
        </p>
      ) : (
        <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="catalogue-grid">
          {tracks.map((t, i) => (
            <TrackCard key={t.track_id} track={t} index={i} />
          ))}
        </div>
      )}
    </main>
  );
};

export default Catalogue;
